import React from "react";
import { ArrowLeft, Calendar, Clock, Tag } from "lucide-react";
import AuthorAvatar from "../../components/AuthorAvatar";
import { formatDate, useStore, KEYS, waLink } from "../../admin/store";

export default function ArticleLayout({
    title,
    date,
    category,
    readTime,
    image,
    content,
    html,
    author,
    relatedArticles = [],
    onBack,
    onArticleClick,
    notice,
}) {
    const settings = useStore(KEYS.settings);

    const openWhatsApp = () => {
        window.open(waLink(settings.whatsapp), "_blank", "noopener");
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {notice}
            {/* Top Navigation / Back Button */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Back to Blog
                    </button>
                    <span className="text-sm text-gray-400 hidden sm:block">MM AutoTech Blog</span>
                </div>
            </header>

            {/* Hero Image */}
            <section className="relative h-[420px] overflow-hidden">
                <img
                    src={image || "/assets/Blog/1.jpg"}
                    alt={title}
                    className="w-full h-full object-cover brightness-75"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
                <div className="absolute bottom-24 left-6 right-6 text-white max-w-3xl">
                    <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg mb-4">{title}</h1>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-200">
                        {author && (
                            <span className="flex items-center gap-2">
                                <AuthorAvatar author={author} size={28} className="ring-2 ring-white/70" />
                                By <strong className="text-white">{author.name}</strong>
                            </span>
                        )}
                        {date && (
                            <span className="flex items-center gap-1">
                                <Calendar size={16} /> {formatDate(date)}
                            </span>
                        )}
                        {readTime && (
                            <span className="flex items-center gap-1">
                                <Clock size={16} /> {readTime}
                            </span>
                        )}
                        {category && (
                            <span className="flex items-center gap-1">
                                <Tag size={16} /> {category}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {/* Article Content */}
            <main className="max-w-4xl mx-auto px-6 py-12 bg-white rounded-2xl shadow-lg -mt-20 relative z-10">
                {html !== undefined ? (
                    <article
                        className="article-content max-w-none text-gray-800"
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                ) : (
                    <article className="article-content max-w-none text-gray-800">{content}</article>
                )}

                {/* About the author */}
                {author && (
                    <div className="mt-12 flex flex-col sm:flex-row gap-4 items-start bg-gray-50 border border-gray-100 rounded-xl p-6">
                        <AuthorAvatar author={author} size={64} />
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Written by</p>
                            <p className="text-lg font-semibold text-gray-900">{author.name}</p>
                            {author.role && <p className="text-sm text-blue-700 mb-2">{author.role}</p>}
                            {author.bio && <p className="text-sm text-gray-600">{author.bio}</p>}
                        </div>
                    </div>
                )}

                {/* Call to Action */}
                <div className="mt-12 bg-blue-600 rounded-xl text-white p-8 shadow-md text-center md:text-left">
                    <h2 className="text-2xl font-semibold mb-2">Need Professional Auto Services?</h2>
                    <p className="text-red-100 mb-6">
                        Book your MOT, diagnostics, or repair service today with our certified garage network.
                    </p>
                    <button
                        onClick={openWhatsApp}
                        className="bg-white text-black font-medium px-6 py-2 rounded-lg hover:bg-gray-100 transition-all"
                    >
                        Chat on WhatsApp
                    </button>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                    <section className="mt-16">
                        <h3 className="text-2xl font-semibold mb-6 text-gray-800">Related Articles</h3>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {relatedArticles.map((article) => (
                                <div
                                    key={article.slug}
                                    onClick={() => onArticleClick(article.slug)}
                                    className="bg-gray-50 border hover:shadow-lg transition-all rounded-xl overflow-hidden cursor-pointer"
                                >
                                    <div className="p-6">
                                        <h4 className="text-xl font-medium text-gray-900 hover:text-red-600 transition-colors">
                                            {article.title}
                                        </h4>
                                        <p className="text-gray-600 text-sm mt-2">{article.excerpt}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
